/**
 * Settings → Organization.
 *
 * The three areas — the workspace itself, the labels learners are organised
 * by, and branding — used to be stacked into one very long scroll, so the tab
 * opened on a wall of forms and everything below the fold was invisible
 * (Aleix, 2026-09-11). They are siblings, not steps, so they read as sub-tabs.
 */
import React from 'react';
import { motion } from 'framer-motion';
import { EASE_OUT } from '../../lib/motion';
import { useAuth } from '../../hooks/useAuth';
import { FEATURE, planHasFeature } from './tiers/tierCatalog';
import SettingsSubTabs, {
  useSettingsSection,
  type SubTabSection,
} from './SettingsSubTabs';
import OrganizationSettings from './OrganizationSettings';
import TeamSettings from './TeamSettings';
import BrandingSettings from './BrandingSettings';

const OrganizationPanel: React.FC = () => {
  const { activeMembership } = useAuth();
  // Branding is a Pro feature; say so on the tab rather than only after the
  // click, so the plan stays legible at a glance like everywhere else.
  const brandingLocked = !planHasFeature(
    FEATURE.branding,
    activeMembership?.tenant.subscription_plan ?? 'free'
  );

  const sections: SubTabSection[] = [
    {
      id: 'workspace',
      label: 'Details',
      hint: 'Workspace name, contracting entity and address',
    },
    {
      id: 'labels',
      label: 'Learner labels',
      hint: 'The roles, departments and locations learners are grouped by',
    },
    {
      id: 'branding',
      label: 'Branding',
      hint: 'Your logo and the colors the platform wears',
      locked: brandingLocked,
      lockedHint: 'Available from the Pro plan',
    },
  ];

  const [current, select] = useSettingsSection('organization', sections);

  return (
    <div>
      <SettingsSubTabs
        ariaLabel="Organization sections"
        sections={sections}
        current={current}
        onSelect={select}
        pillId="org-section-pill"
      />

      <motion.div
        key={current}
        role="tabpanel"
        id={`section-panel-${current}`}
        aria-labelledby={`section-tab-${current}`}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15, ease: EASE_OUT }}
      >
        {current === 'workspace' && <OrganizationSettings />}
        {current === 'labels' && <TeamSettings />}
        {current === 'branding' && <BrandingSettings />}
      </motion.div>
    </div>
  );
};

export default OrganizationPanel;
